import { uiText, useUiLanguage } from '@/i18n/interface';
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Play, Clock, Award, Filter } from 'lucide-react';
import { toast } from "@/hooks/use-toast";
import { PremiumFeatureGate } from '@/components/premium/PremiumFeatureGate';

interface OnDemandTraining {
  id: string;
  title: string;
  description: string | null;
  video_url: string;
  thumbnail_url: string | null;
  belt_level_id: string | null;
  skill_category: string;
  duration_minutes: number | null;
  difficulty_level: string;
  created_at: string;
  belt_level?: {
    name: string;
    color: string;
  } | null;
  progress?: {
    progress_percentage: number;
    completed_at: string | null;
    last_watched_at: string;
  } | null;
}

const OnDemandTrainingTab = () => {
  useUiLanguage();
  const { t } = useTranslation();
  const { profile } = useAuth();
  const [trainings, setTrainings] = useState<OnDemandTraining[]>([]);
  const [filteredTrainings, setFilteredTrainings] = useState<OnDemandTraining[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [skillFilter, setSkillFilter] = useState<string>('all');
  const [difficultyFilter, setDifficultyFilter] = useState<string>('all');

  useEffect(() => {
    fetchOnDemandTrainings();
  }, [profile]);

  useEffect(() => {
    applyFilters();
  }, [trainings, skillFilter, difficultyFilter]);

  const fetchOnDemandTrainings = async () => {
    if (!profile?.id) return;

    try {
      setIsLoading(true);

      const { data, error } = await supabase
        .from('on_demand_training')
        .select(`
          *,
          belt_level:belt_levels(name, color),
          progress:on_demand_progress(progress_percentage, completed_at, last_watched_at)
        `)
        .eq('is_active', true)
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Transform the data to handle progress array
      const transformedData = data?.map(item => ({
        ...item,
        progress: item.progress && item.progress.length > 0 ? item.progress[0] : null
      })) || [];

      setTrainings(transformedData);
    } catch (error) {
      console.error('Error fetching on-demand trainings:', error);
      toast({
        title: uiText("Error"),
        description: uiText("Failed to load training videos."),
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = trainings;

    if (skillFilter !== 'all') {
      filtered = filtered.filter(training => training.skill_category === skillFilter);
    }

    if (difficultyFilter !== 'all') {
      filtered = filtered.filter(training => training.difficulty_level === difficultyFilter);
    }

    setFilteredTrainings(filtered);
  };

  const handleWatchVideo = async (training: OnDemandTraining) => {
    // Update last watched timestamp
    try {
      await supabase
        .from('on_demand_progress')
        .upsert({
          student_id: profile?.id!,
          training_id: training.id,
          last_watched_at: new Date().toISOString(),
          progress_percentage: training.progress?.progress_percentage || 0
        });

      // Open video in new tab/window
      window.open(training.video_url, '_blank');
    } catch (error) {
      console.error('Error updating watch progress:', error);
      // Still open the video even if tracking fails
      window.open(training.video_url, '_blank');
    }
  };

  const getDifficultyColor = (level: string) => {
    switch (level) {
      case 'beginner': return 'bg-green-100 text-green-800';
      case 'intermediate': return 'bg-yellow-100 text-yellow-800';
      case 'advanced': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getSkillCategoryIcon = (category: string) => {
    switch (category) {
      case 'kata': return '🥋';
      case 'kumite': return '🥊';
      case 'basics': return '👊';
      case 'conditioning': return '💪';
      case 'philosophy': return '🧘';
      default: return '📚';
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <div className="text-center space-y-2">
            <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full mx-auto"></div>
            <p className="text-muted-foreground">{uiText("Loading training videos...")}</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <PremiumFeatureGate
      featureName={uiText("On-Demand Training")}
      description={uiText("Access a comprehensive library of training videos, track your progress, and learn at your own pace. Get personalized training content based on your belt level and skill development goals.")}
    >
      <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Play className="h-5 w-5" />
            {uiText("On-Demand Training")}</CardTitle>
          <CardDescription>
            {uiText("Access training videos anytime, anywhere. Track your progress and improve at your own pace.")}</CardDescription>
        </CardHeader>

        <CardContent>
          <div className="flex gap-4 mb-6">
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4" />
              <Select value={skillFilter} onValueChange={setSkillFilter}>
                <SelectTrigger className="w-[140px]">
                  <SelectValue placeholder={uiText("Skill")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{uiText("All Skills")}</SelectItem>
                  <SelectItem value="kata">{uiText("Kata")}</SelectItem>
                  <SelectItem value="kumite">{uiText("Kumite")}</SelectItem>
                  <SelectItem value="basics">{uiText("Basics")}</SelectItem>
                  <SelectItem value="conditioning">{uiText("Conditioning")}</SelectItem>
                  <SelectItem value="philosophy">{uiText("Philosophy")}</SelectItem>
                  <SelectItem value="other">{uiText("Other")}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <Select value={difficultyFilter} onValueChange={setDifficultyFilter}>
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder={uiText("Difficulty")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{uiText("All Levels")}</SelectItem>
                <SelectItem value="beginner">{uiText("Beginner")}</SelectItem>
                <SelectItem value="intermediate">{uiText("Intermediate")}</SelectItem>
                <SelectItem value="advanced">{uiText("Advanced")}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {filteredTrainings.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Play className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">{uiText("No Training Videos")}</h3>
            <p className="text-muted-foreground text-center">
              {trainings.length === 0
                ? uiText("No training videos are available yet. Check back soon!")
                : uiText("No videos match your current filters. Try adjusting your selection.")
              }
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredTrainings.map((training) => (
            <Card key={training.id} className="overflow-hidden hover:shadow-lg transition-shadow">
              <div className="relative">
                {training.thumbnail_url ? (
                  <img
                    src={training.thumbnail_url}
                    alt={training.title}
                    className="w-full h-32 object-cover"
                    onError={(e) => {
                      // Hide broken image and show fallback
                      e.currentTarget.style.display = 'none';
                      const fallback = e.currentTarget.nextElementSibling as HTMLElement;
                      if (fallback) fallback.style.display = 'flex';
                    }}
                  />
                ) : null}

                <div
                  className="w-full h-32 bg-gradient-to-br from-primary/10 to-primary/5 flex items-center justify-center"
                  style={{ display: training.thumbnail_url ? 'none' : 'flex' }}
                >
                  <span className="text-4xl">{getSkillCategoryIcon(training.skill_category)}</span>
                </div>

                {training.progress && training.progress.progress_percentage > 0 && (
                  <div className="absolute bottom-0 left-0 right-0 bg-black/50 text-white text-xs px-2 py-1">
                    <Progress
                      value={training.progress.progress_percentage}
                      className="h-1 mb-1"
                    />
                    <span>{training.progress.progress_percentage}{uiText("% complete")}</span>
                  </div>
                )}
              </div>

              <CardContent className="p-4">
                <div className="space-y-3">
                  <div>
                    <h3 className="font-semibold line-clamp-2">{training.title}</h3>
                    {training.description && (
                      <p className="text-sm text-muted-foreground line-clamp-2 mt-1">
                        {training.description}
                      </p>
                    )}
                  </div>

                  <div className="flex flex-wrap gap-2">
                    <Badge variant="outline" className={getDifficultyColor(training.difficulty_level)}>
                      {training.difficulty_level}
                    </Badge>

                    <Badge variant="outline">
                      {training.skill_category}
                    </Badge>

                    {training.belt_level && (
                      <Badge variant="outline" style={{ borderColor: training.belt_level.color }}>
                        {training.belt_level.name}
                      </Badge>
                    )}
                  </div>

                  <div className="flex items-center justify-between">
                    {training.duration_minutes && (
                      <div className="flex items-center gap-1 text-sm text-muted-foreground">
                        <Clock className="h-3 w-3" />
                        {training.duration_minutes}m
                      </div>
                    )}

                    {training.progress?.completed_at && (
                      <div className="flex items-center gap-1 text-sm text-green-600">
                        <Award className="h-3 w-3" />
                        {uiText("Completed")}</div>
                    )}
                  </div>

                  <Button
                    onClick={() => handleWatchVideo(training)}
                    className="w-full"
                    size="sm"
                  >
                    <Play className="h-4 w-4 mr-2" />
                    {training.progress?.progress_percentage ? uiText("Continue") : uiText("Start")} {' '}{uiText("Training")}</Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
    </PremiumFeatureGate>
  );
};

export default OnDemandTrainingTab;